import { useEffect, useMemo, useRef, useState } from "react";
import type {
  CircuitRoutingState,
  RouteRequest,
  UseCircuitRouting,
} from "./types";

const SETTLE_MS = 600;
const MAX_CIRCUITS_PER_REQUEST = 150;

export const useCircuitRouting: UseCircuitRouting = ({
  requests,
  routeCache,
  floorPlanId,
  trayTypes,
  requestReroute,
  isEnabled,
}) => {
  const [routing, setRouting] = useState<CircuitRoutingState>({
    phase: "idle",
  });
  // Bumped when a batch lands, so requests that arrived while it was out are sent next.
  const [landedBatches, setLandedBatches] = useState(0);
  const inFlightRef = useRef(false);
  const latest = useRef({ requestReroute, trayTypes, floorPlanId });
  latest.current = { requestReroute, trayTypes, floorPlanId };

  const { cache, addAnswers, isFailed } = routeCache;
  const open = useMemo(
    () =>
      requests.filter(
        (request) =>
          !cache[request.circuit.ref] && !isFailed(request.circuit.ref),
      ),
    [requests, cache, isFailed],
  );
  const openKey = open.map((request) => request.circuit.ref).join("\n");
  const openRef = useRef<RouteRequest[]>(open);
  openRef.current = open;

  useEffect(() => {
    if (inFlightRef.current) return;
    if (!isEnabled || floorPlanId === undefined || !openKey) {
      setRouting((current) =>
        current.phase === "idle" ? current : { phase: "idle" },
      );
      return;
    }
    setRouting((current) =>
      current.phase === "waiting" ? current : { phase: "waiting" },
    );
    const timer = window.setTimeout(async () => {
      const pending = openRef.current;
      if (!pending.length || inFlightRef.current) return;
      // One surcharge per request: the router bills a whole request at one Zuschlag.
      const surcharge = pending[0].noTraySurchargePct;
      const batch = pending
        .filter((request) => request.noTraySurchargePct === surcharge)
        .slice(0, MAX_CIRCUITS_PER_REQUEST);
      const keys = batch.map((request) => request.circuit.ref);
      inFlightRef.current = true;
      setRouting({ phase: "inFlight", keys });
      try {
        const {
          requestReroute: ask,
          trayTypes: trays,
          floorPlanId: plan,
        } = latest.current;
        if (plan === undefined) return;
        const result = await ask({
          floorPlanId: plan,
          noTraySurchargePct: surcharge,
          trayTypes: trays,
          circuits: batch.map((request) => request.circuit),
        });
        addAnswers(
          keys.map((key) => {
            const answer = result.circuits.find(
              (circuit) => circuit.ref === key,
            );
            return [
              key,
              answer?.paths.length
                ? {
                    paths: answer.paths,
                    billedM: answer.billedM,
                    onTrayM: answer.onTrayM,
                    routingMode: answer.routingMode,
                    trayTypesRidden: answer.trayTypesRidden,
                    wallCrossings: answer.wallCrossings,
                  }
                : null,
            ];
          }),
        );
      } catch {
        addAnswers(keys.map((key) => [key, null]));
      } finally {
        inFlightRef.current = false;
        setRouting({ phase: "idle" });
        setLandedBatches((count) => count + 1);
      }
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [openKey, isEnabled, floorPlanId, landedBatches, addAnswers]);

  return { routing };
};
